import { forwardRef, ButtonHTMLAttributes } from 'react';
import { clsx } from 'clsx';
import { Loader2 } from 'lucide-react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
type Size = 'xs' | 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: React.ReactNode;
}

const variantClasses: Record<Variant, string> = {
  primary:   'text-white hover:opacity-90 focus-visible:ring-brand-500',
  secondary: 'hover:opacity-90 focus-visible:ring-nucleo-mute',
  ghost:     'hover:opacity-90 focus-visible:ring-nucleo-mute',
  danger:    'hover:opacity-90 focus-visible:ring-nucleo-danger',
  outline:   'hover:opacity-90 focus-visible:ring-nucleo-mute',
};

const variantStyles: Record<Variant, React.CSSProperties> = {
  primary:   { background: 'var(--nucleo-accent)', color: '#fff', border: '1px solid var(--nucleo-accent)' },
  secondary: { background: 'var(--nucleo-surface)', border: '1px solid var(--nucleo-line-mid)', color: 'var(--nucleo-ink)' },
  ghost:     { background: 'transparent', border: '1px solid transparent', color: 'var(--nucleo-mute)' },
  danger:    { background: 'var(--nucleo-danger)', color: '#fff', border: '1px solid var(--nucleo-danger)' },
  outline:   { background: 'transparent', border: '1px solid var(--nucleo-line-mid)', color: 'var(--nucleo-ink)' },
};

const sizeClasses: Record<Size, string> = {
  xs: 'px-2 text-[11px] gap-1',
  sm: 'px-2.5 text-[12px] gap-1.5',
  md: 'px-3.5 text-[12.5px] gap-1.5',
  lg: 'px-[18px] text-sm gap-2',
};

const sizeStyles: Record<Size, React.CSSProperties> = {
  xs: { height: 24, letterSpacing: '-0.05px' },
  sm: { height: 26, letterSpacing: '-0.05px' },
  md: { height: 32, letterSpacing: '-0.05px' },
  lg: { height: 40, letterSpacing: '-0.05px' },
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', loading, icon, children, className, disabled, style, ...props }, ref) => (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={clsx(
        'inline-flex items-center justify-center font-medium transition-opacity',
        'focus:outline focus:outline-2 focus:outline-[var(--nucleo-accent)] focus:outline-offset-2',
        'disabled:opacity-40 disabled:cursor-not-allowed',
        variantClasses[variant],
        sizeClasses[size],
        className
      )}
      style={{ ...variantStyles[variant], ...sizeStyles[size], fontWeight: 500, ...style }}
      {...props}
    >
      {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : icon}
      {children}
    </button>
  )
);
Button.displayName = 'Button';
